<script lang="ts">
	import { invalidate } from '$app/navigation';
	import { page } from '$app/state';
	import { env } from '$env/dynamic/public';
	import {
		type Currency$options,
		type DistanceSystem$options,
		type ItineraryConstraint$options,
		UpdateUserTripPreferencesStore,
	} from '$houdini';
	import Select from '$lib/components/atoms/select/Select.svelte';
	import Icon from '$lib/components/icons/Icon.svelte';
	import {
		getCurrencyOptions,
		getDefaultTripOptions,
		getDistanceSystemOptions,
		getTripConstraintOptions,
	} from '$lib/components/itinerary/tripOptions';
	import SegmentControl from '$lib/components/molecules/segment-control/SegmentControl.svelte';
	import ProfileCarCard from '$lib/components/profile/ProfileCarCard.svelte';
	import * as m from '$paraglide/messages';

	let updateUserTripPreferencesStore: UpdateUserTripPreferencesStore;

	const { userProfileStore } = page.data;
	const isLoading = $derived($userProfileStore?.fetching || !$userProfileStore?.data);
	const userTripPreferences = $derived($userProfileStore?.data?.me.tripPreferences);
	const defaultPreferences = getDefaultTripOptions();
	let selectedTripConstraint
		= $derived(userTripPreferences?.tripConstraint ?? defaultPreferences.tripConstraint);
	let selectedCurrency = $derived(userTripPreferences?.currency ?? defaultPreferences.currency);
	let selectedDistanceSystem
		= $derived(userTripPreferences?.distanceSystem ?? defaultPreferences.distanceSystem);

	function updateUserTripPreferences({
		distanceSystem,
		tripConstraint,
		currency,
	}: {
		distanceSystem?: DistanceSystem$options;
		tripConstraint?: ItineraryConstraint$options;
		currency?: Currency$options;
	}) {
		if (!updateUserTripPreferencesStore) {
			updateUserTripPreferencesStore = new UpdateUserTripPreferencesStore();
		}

		selectedDistanceSystem = distanceSystem ?? selectedDistanceSystem;
		selectedTripConstraint = tripConstraint ?? selectedTripConstraint;
		selectedCurrency = currency ?? selectedCurrency;
		updateUserTripPreferencesStore.mutate({
			input: {
				distanceSystem: selectedDistanceSystem,
				tripConstraint: selectedTripConstraint,
				currency: selectedCurrency,
			},
		});
	}

	function reload() {
		invalidate('loadUser:init');
	}
</script>

<div class="flex flex-col px-2 md:px-4 items-start w-full">
	<h1 class="text-title text-coloredneutral hidden md:block my-0">
		{m.profile__tabs__settings__title()}
	</h1>
	<div class="flex flex-row items-center gap-1 bg-neutral-soft rd-1 text-coloredneutral p-2 mt-1">
		<Icon icon="info" class="shrink-0" />
		<span class="text-sm">{m.profile__tabs__settings__banner()}</span>
	</div>
	<div class="flex flex-col w-full gap-6 md:gap-4 mt-6 md:mt-8">
		{#if env.PUBLIC_FEATURE_REGISTER_MY_CAR_ENABLED === 'true'}
			<ProfileCarCard onUpdateCar={reload} />
		{/if}
		{#if !isLoading}
			<Select
				label={m.itinerary__options__trip_constraint()}
				options={getTripConstraintOptions()}
				defaultValue={selectedTripConstraint}
				ariaLabel={m.itinerary__options__trip_constraint()}
				dropDownSameWidth
				onSelectedChange={constraint => updateUserTripPreferences({ tripConstraint: constraint })}
			/>
			<Select
				label={m.itinerary__options__currency()}
				options={getCurrencyOptions()}
				defaultValue={selectedCurrency}
				ariaLabel={m.itinerary__options__currency()}
				dropDownSameWidth
				onSelectedChange={currency => updateUserTripPreferences({ currency })}
			/>
			<div class="flex flex-col gap-1 text-sm text-coloredneutral">
				{m.itinerary__options__distance_unit()}
				<SegmentControl
					options={getDistanceSystemOptions()}
					defaultSelectedOption={selectedDistanceSystem}
					onSelectOption={distanceSystem => updateUserTripPreferences({ distanceSystem })}
				/>
			</div>
		{/if}
	</div>
</div>
